import React, { useMemo, useState } from "react";
import { Search, Filter, RefreshCw, X, ChevronRight, ChevronDown } from "lucide-react";

const norm = (v) => String(v || "").trim().toLowerCase();
const normStatus = (s) => String(s || "").trim().toLowerCase();

const PDLList = ({ pdls = [], onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all | connected | pending | token_expired | error
  const [sortBy, setSortBy] = useState("created_at");
  const [refreshing, setRefreshing] = useState(false);

  // filtres colonne (optionnels, comme PCE)
  const [columnFilters, setColumnFilters] = useState({
    company_name: "",
    site_name: "",
    supplier_name: "",
    pdl_number: "",
    contract_power: "",
    last_sync_date: "",
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh?.();
    setTimeout(() => setRefreshing(false), 800);
  };

  const handleColumnFilterChange = (columnName, value) => {
    setColumnFilters((prev) => ({ ...prev, [columnName]: value }));
  };

  const resetAllFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setColumnFilters({
      company_name: "",
      site_name: "",
      supplier_name: "",
      pdl_number: "",
      contract_power: "",
      last_sync_date: "",
    });
  };

  const getStatusCount = (status) => {
    if (status === "all") return pdls.length;

    if (status === "pending") {
      return pdls.filter((p) => {
        const st = normStatus(p.connection_status);
        return st === "not_connected" || st === "pending";
      }).length;
    }

    return pdls.filter((p) => normStatus(p.connection_status) === status).length;
  };

  const hasActiveFilters =
    !!searchTerm ||
    filterStatus !== "all" ||
    Object.values(columnFilters).some((v) => v && v.trim() !== "");

  const filteredPDLs = useMemo(() => {
    const term = norm(searchTerm);

    return (pdls || [])
      .filter((pdl) => {
        // --- recherche globale
        const matchesSearch = !term
          ? true
          : [
              pdl.site_name,
              pdl.pdl_number,
              pdl.address?.city,
              pdl.company_name,
              pdl.supplier_name,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(term);

        // --- filtre statut
        const st = normStatus(pdl.connection_status);

        const matchesStatus =
          filterStatus === "all"
            ? true
            : filterStatus === "pending"
              ? st === "not_connected" || st === "pending"
              : st === filterStatus;

        // --- filtres colonnes (comme PCE)
        const matchesCompany =
          !columnFilters.company_name ||
          norm(pdl.company_name).includes(norm(columnFilters.company_name));

        const matchesSite =
          !columnFilters.site_name ||
          norm(pdl.site_name).includes(norm(columnFilters.site_name)) ||
          String(pdl.pdl_number || "").includes(columnFilters.site_name);

        const matchesSupplier =
          !columnFilters.supplier_name ||
          norm(pdl.supplier_name).includes(norm(columnFilters.supplier_name));

        const matchesPdlNumber =
          !columnFilters.pdl_number ||
          String(pdl.pdl_number || "").includes(columnFilters.pdl_number);

        const matchesPower =
          !columnFilters.contract_power ||
          String(pdl.contract_power || pdl.contract_power_kva || "")
            .includes(columnFilters.contract_power);

        const matchesLastSync =
          !columnFilters.last_sync_date ||
          String(pdl.last_sync_date || "").includes(columnFilters.last_sync_date);

        return (
          matchesSearch &&
          matchesStatus &&
          matchesCompany &&
          matchesSite &&
          matchesSupplier &&
          matchesPdlNumber &&
          matchesPower &&
          matchesLastSync
        );
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "site_name":
            return (a.site_name || "").localeCompare(b.site_name || "", "fr", { sensitivity: "base" });

          case "status":
            return (a.connection_status || "").localeCompare(b.connection_status || "", "fr", { sensitivity: "base" });

          case "power":
            return (Number(b.contract_power || b.contract_power_kva) || 0) - (Number(a.contract_power || a.contract_power_kva) || 0);

          case "last_sync_date": {
            const da = a?.last_sync_date ? new Date(a.last_sync_date).getTime() : 0;
            const db = b?.last_sync_date ? new Date(b.last_sync_date).getTime() : 0;
            return db - da;
          }

          case "company_name":
            return (a.company_name || "").localeCompare(b.company_name || "", "fr", { sensitivity: "base" });

          case "created_at":
          default:
            return (new Date(b.created_at).getTime() || 0) - (new Date(a.created_at).getTime() || 0);
        }
      });
  }, [pdls, searchTerm, filterStatus, sortBy, columnFilters]);

  // Regroupement accordéon par entreprise (company_name). Tous fermés par
  // défaut, l'utilisateur déplie ce qu'il veut voir.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const groupedPDLs = useMemo(() => {
    const map = new Map();
    for (const pdl of filteredPDLs) {
      const key = pdl.account_id || pdl.company_name || `__solo__${pdl.id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          companyName: pdl.company_name || "Sans entreprise",
          pdls: [],
        });
      }
      map.get(key).pdls.push(pdl);
    }
    return Array.from(map.values());
  }, [filteredPDLs]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (!pdls || pdls.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-xs font-medium text-gray-900">Aucun PDL configuré</div>
        <div className="text-xs text-gray-600 mt-1">Aucun Point de Livraison n’a été trouvé.</div>
      </div>
    );
  }

  const badgeForStatus = (raw) => {
    const st = normStatus(raw);

    if (st === "connected") {
      return <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Connecté</span>;
    }
    if (st === "token_expired") {
      return <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700">Token expiré</span>;
    }
    if (st === "error") {
      return <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">Erreur</span>;
    }
    if (st === "not_connected" || st === "pending") {
      return <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">En attente</span>;
    }
    return <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{raw || "—"}</span>;
  };

  return (
    <div className="space-y-4 p-4">
      {/* ===== Toolbar (comme PCEList) ===== */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-col lg:flex-row gap-3 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Rechercher par nom, PDL ou ville..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Effacer la recherche"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tous ({getStatusCount("all")})</option>
                <option value="connected">Connectés ({getStatusCount("connected")})</option>
                <option value="pending">En attente ({getStatusCount("pending")})</option>
                <option value="token_expired">Token expiré ({getStatusCount("token_expired")})</option>
                <option value="error">Erreur ({getStatusCount("error")})</option>
              </select>
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="created_at">Date de création</option>
              <option value="site_name">Nom du site</option>
              <option value="status">Statut</option>
              <option value="power">Puissance</option>
              <option value="last_sync_date">Dernière synchro</option>
              <option value="company_name">Entreprise</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={resetAllFilters}
                className="flex items-center gap-2 px-3 py-1.5 text-red-600 border border-red-300 rounded-md hover:bg-red-50 text-xs transition-colors"
                title="Réinitialiser tous les filtres"
              >
                <X className="h-3 w-3" />
                Réinitialiser
              </button>
            )}

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 text-xs transition-colors"
            >
              <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
              Actualiser
            </button>
          </div>
        </div>

        {/* mini-stats */}
        <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full" />
            {getStatusCount("connected")} connectés
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-yellow-500 rounded-full" />
            {getStatusCount("pending")} en attente
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-red-500 rounded-full" />
            {getStatusCount("error")} en erreur
          </span>

          {hasActiveFilters && (
            <span className="flex items-center gap-1 text-blue-600 font-medium">
              <Filter className="h-3 w-3" />
              {filteredPDLs.length} résultat{filteredPDLs.length > 1 ? "s" : ""} filtré{filteredPDLs.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      {/* ===== Table ===== */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {/* Filtres colonnes (optionnel mais “structure PCE”) */}
        <div className="p-3 border-b bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-2">
            <input
              value={columnFilters.company_name}
              onChange={(e) => handleColumnFilterChange("company_name", e.target.value)}
              placeholder="Entreprise…"
              className="text-xs px-2 py-2 border rounded"
            />
            <input
              value={columnFilters.site_name}
              onChange={(e) => handleColumnFilterChange("site_name", e.target.value)}
              placeholder="Site / PDL…"
              className="text-xs px-2 py-2 border rounded"
            />
            <input
              value={columnFilters.supplier_name}
              onChange={(e) => handleColumnFilterChange("supplier_name", e.target.value)}
              placeholder="Fournisseur…"
              className="text-xs px-2 py-2 border rounded"
            />
            <input
              value={columnFilters.pdl_number}
              onChange={(e) => handleColumnFilterChange("pdl_number", e.target.value)}
              placeholder="N° PDL…"
              className="text-xs px-2 py-2 border rounded"
            />
            <input
              value={columnFilters.contract_power}
              onChange={(e) => handleColumnFilterChange("contract_power", e.target.value)}
              placeholder="Puissance…"
              className="text-xs px-2 py-2 border rounded"
            />
            <input
              value={columnFilters.last_sync_date}
              onChange={(e) => handleColumnFilterChange("last_sync_date", e.target.value)}
              placeholder="Dernière synchro…"
              className="text-xs px-2 py-2 border rounded"
            />
          </div>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-white sticky top-0 z-10">
              <tr className="text-left text-xs text-gray-600 border-b">
                <th className="px-3 py-2">Entreprise</th>
                <th className="px-3 py-2">Site</th>
                <th className="px-3 py-2">PDL</th>
                <th className="px-3 py-2">Fournisseur</th>
                <th className="px-3 py-2">Puissance</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2">Dernière synchro</th>
              </tr>
            </thead>
            <tbody>
              {groupedPDLs.flatMap((group) => {
                // Cas mono-PDL : on rend la ligne directement, sans header de
                // groupe ni chevron (demande utilisateur : l'accordéon ne sert
                // que pour les entreprises avec plusieurs PDL).
                if (group.pdls.length === 1) {
                  const pdl = group.pdls[0];
                  return [(
                    <tr key={pdl.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 text-xs text-gray-900">{pdl.company_name || "—"}</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{pdl.site_name || "—"}</td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-900">{pdl.pdl_number || "—"}</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{pdl.supplier_name || "—"}</td>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        {pdl.contract_power ?? pdl.contract_power_kva ?? "—"}
                      </td>
                      <td className="px-3 py-2">{badgeForStatus(pdl.connection_status)}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        {pdl.last_sync_date ? new Date(pdl.last_sync_date).toLocaleString("fr-FR") : "—"}
                      </td>
                    </tr>
                  )];
                }

                // Cas multi-PDL : header de groupe + accordéon
                const isOpen = expandedGroups.has(group.key);
                const headerRow = (
                  <tr
                    key={`grp-${group.key}`}
                    onClick={() => toggleGroup(group.key)}
                    className="bg-slate-100 hover:bg-slate-200 cursor-pointer border-b border-slate-300 select-none"
                  >
                    <td colSpan={7} className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        <span className="text-xs font-semibold text-gray-800">{group.companyName}</span>
                        <span className="text-[10px] text-gray-500">
                          {group.pdls.length} PDLs
                        </span>
                      </div>
                    </td>
                  </tr>
                );
                if (!isOpen) return [headerRow];
                const childRows = group.pdls.map((pdl) => (
                  <tr key={pdl.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 text-[10px] text-gray-400 pl-8">↳</td>
                    <td className="px-3 py-2 text-xs text-gray-900">{pdl.site_name || "—"}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-900">{pdl.pdl_number || "—"}</td>
                    <td className="px-3 py-2 text-xs text-gray-900">{pdl.supplier_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-gray-900">
                      {pdl.contract_power ?? pdl.contract_power_kva ?? "—"}
                    </td>
                    <td className="px-3 py-2">{badgeForStatus(pdl.connection_status)}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">
                      {pdl.last_sync_date ? new Date(pdl.last_sync_date).toLocaleString("fr-FR") : "—"}
                    </td>
                  </tr>
                ));
                return [headerRow, ...childRows];
              })}

              {groupedPDLs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-xs text-gray-600">
                    Aucun résultat avec ces filtres.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PDLList;
